<template>
  <el-dialog
    :visible.sync="visible"
    :show-close="true"
    custom-class="product-download-dialog"
    width="90%"
    @close="handleClose"
  >
    <div class="dialog-header">
      <div class="dialog-top">
        <img :src="require('@/assets/logo-active.png')" :alt="getText('productDownloadDialog.logoAlt', '亚科Logo')" class="dialog-logo">
        <div class="dialog-title">
          {{ getText('serviceDialog.hotline', '免费服务热线：') }}<span> {{ currentLanguage !== 'zh' ? '+86-15062454963' : '13451648594' }} </span>
        </div>
      </div>
      <div class="dialog-bottom">
        {{ getText('productDownloadDialog.description', '请填写以下信息以下载文档') }}
      </div>
    </div>

    <el-form
      ref="downloadForm"
      v-loading="loading"
      :model="formData"
      :rules="rules"
      label-position="top"
      class="download-form"
    >
      <el-form-item :label="getText('productDownloadDialog.form.companyName', '公司名称')" prop="companyName">
        <el-input
          v-model="formData.companyName"
          :placeholder="getText('productDownloadDialog.form.companyNamePlaceholder', '请输入公司名称')"
        />
      </el-form-item>

      <el-form-item :label="getText('productDownloadDialog.form.email', '邮箱')" prop="email">
        <el-input
          v-model="formData.email"
          :placeholder="getText('productDownloadDialog.form.emailPlaceholder', '请输入邮箱')"
        />
      </el-form-item>

      <el-form-item :label="getText('productDownloadDialog.form.mobile', '电话')" prop="mobile">
        <el-input
          v-model="formData.mobile"
          :placeholder="getText('productDownloadDialog.form.mobilePlaceholder', '请输入电话')"
        />
      </el-form-item>

      <el-form-item :label="getText('productDownloadDialog.form.qq', 'QQ')" prop="qq">
        <el-input
          v-model="formData.qq"
          :placeholder="getText('productDownloadDialog.form.qqPlaceholder', '请输入QQ（选填）')"
        />
      </el-form-item>

      <el-form-item :label="getText('productDownloadDialog.form.wechat', '微信')" prop="wechat">
        <el-input
          v-model="formData.wechat"
          :placeholder="getText('productDownloadDialog.form.wechatPlaceholder', '请输入微信（选填）')"
        />
      </el-form-item>

      <el-form-item class="form-footer">
        <el-button type="primary" :loading="loading" @click="handleSubmit">{{ getText('productDownloadDialog.form.submit', '提交') }}</el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script>
import { downloadRecord } from '@/api/products'
import { mapState } from 'vuex'

export default {
  name: 'ProductDownloadDialog',
  props: {
    productId: {
      type: [Number, String],
      default: null
    },
    productName: {
      type: String,
      default: ''
    },
    attachmentId: {
      type: [Number, String],
      default: null
    },
    attachmentName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      submitContext: null,
      formData: {
        companyName: '',
        email: '',
        mobile: '',
        qq: '',
        wechat: ''
      }
    }
  },
  computed: {
    ...mapState({
      currentLanguage: state => state?.app?.language || 'zh'
    }),
    rules() {
      return {
        companyName: [
          { required: true, message: this.getText('productDownloadDialog.validation.companyNameRequired', '请输入公司名称'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.getText('productDownloadDialog.validation.emailRequired', '请输入邮箱'), trigger: 'blur' },
          { type: 'email', message: this.getText('productDownloadDialog.validation.emailInvalid', '请输入有效的邮箱地址'), trigger: 'blur' }
        ],
        mobile: [
          { required: true, message: this.getText('productDownloadDialog.validation.mobileRequired', '请输入电话'), trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    getText(key, fallback) {
      try {
        if (this.$i18n && this.$i18n.t) {
          return this.$i18n.t(key)
        }
        return fallback
      } catch (error) {
        console.warn(`Translation failed for key: ${key}`, error)
        return fallback
      }
    },

    buildCurrentContext(context = null) {
      return {
        productId: context?.productId || this.productId,
        productName: context?.productName || this.productName,
        attachmentId: context?.attachmentId || this.attachmentId,
        attachmentName: context?.attachmentName || this.attachmentName
      }
    },

    show(context = {}) {
      if (this.loading) {
        return
      }
      const currentContext = this.buildCurrentContext(context)
      if (!currentContext.attachmentId) {
        return
      }

      this.submitContext = currentContext
      const cachedProfile = this.loadCachedProfile()
      if (this.isCachedProfileValid(cachedProfile)) {
        this.formData = { ...cachedProfile }
        this.submitDownloadRecord(false)
          .catch(error => {
            console.error('缓存提交下载记录失败:', error)
            this.$message.error(this.getText('productDownloadDialog.messages.submitFailedRetry', '提交失败，请稍后重试'))
          })
        return
      }

      this.visible = true
    },

    handleClose(emitCancel = true) {
      this.visible = false
      this.submitContext = null
      if (emitCancel) {
        this.$emit('download-cancel')
      }
      if (this.$refs.downloadForm) {
        this.$refs.downloadForm.resetFields()
      }
    },

    loadCachedProfile() {
      try {
        const cached = localStorage.getItem('productDownloadProfile')
        return cached ? JSON.parse(cached) : null
      } catch (error) {
        console.error('Failed to load cached profile:', error)
        return null
      }
    },

    saveCachedProfile() {
      try {
        const profile = {
          companyName: this.formData.companyName,
          email: this.formData.email,
          mobile: this.formData.mobile,
          qq: this.formData.qq,
          wechat: this.formData.wechat
        }
        localStorage.setItem('productDownloadProfile', JSON.stringify(profile))
      } catch (error) {
        console.error('Failed to save profile:', error)
      }
    },

    isCachedProfileValid(cachedProfile) {
      if (!cachedProfile) {
        return false
      }
      return !!(cachedProfile.companyName && cachedProfile.email && cachedProfile.mobile)
    },

    handleSubmit() {
      this.submitDownloadRecord(true)
        .catch(error => {
          console.error('提交表单失败:', error)
          this.$message.error(this.getText('productDownloadDialog.messages.submitFailedRetry', '提交失败，请稍后重试'))
        })
    },

    async submitDownloadRecord(needValidate = true) {
      if (this.loading) {
        return
      }
      if (needValidate) {
        const valid = await this.$refs.downloadForm.validate()
          .catch(error => {
            console.error('表单验证失败:', error)
            return false
          })

        if (!valid) {
          return
        }
      }

      const currentContext = this.submitContext || this.buildCurrentContext()

      this.loading = true
      try {
        const payload = {
          productId: currentContext.productId,
          productName: currentContext.productName,
          attachmentId: currentContext.attachmentId,
          attachmentName: currentContext.attachmentName,
          terminalType: 2,
          companyName: this.formData.companyName,
          email: this.formData.email,
          mobile: this.formData.mobile,
          qq: this.formData.qq,
          wechat: this.formData.wechat
        }

        const response = await downloadRecord(payload)

        if (response.success) {
          this.saveCachedProfile()
          this.$emit('download-continue')
          if (this.visible) {
            this.handleClose(false)
          } else {
            this.submitContext = null
          }
        } else {
          this.$emit('download-failed')
          this.$message.error(response.msg || this.getText('productDownloadDialog.messages.submitFailed', '提交失败'))
        }
      } catch (error) {
        this.$emit('download-failed')
        throw error
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.product-download-dialog {
  :deep(.el-dialog) {
    margin: 20px auto !important;
    max-width: 400px;
    border-radius: 12px;
  }

  :deep(.el-dialog__header) {
    display: none;
  }

  /deep/ .el-dialog__body {
    padding: 0 !important;
  }

  .dialog-header {
    padding: 10px 10px 0;
    background: #f8f9fa;
    border-radius: 6px 6px 0 0;

    .dialog-top {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 10px;

      .dialog-logo {
        width: 80px;
        height: auto;
        margin-bottom: 15px;
      }

      .dialog-title {
        font-size: 14px;
        color: #333;
        text-align: center;
        line-height: 1.4;

        span {
          color: var(--primary-color);
          font-weight: bold;
        }
      }
    }

    .dialog-bottom {
      font-size: 14px;
      color: #666;
      line-height: 1.5;
      text-align: center;
      padding-bottom: 20px;
    }
  }

  .download-form {
    padding: 20px;

    .form-footer {
      margin-top: 20px;
      margin-bottom: 0;
      text-align: center;

      :deep(.el-button) {
        width: 120px;
        border-radius: 20px;
      }
    }
  }
}
</style>
